import { useContext } from 'react';
import dynamic from 'next/dynamic';

import { Info } from '@/icons';
import { NextLink, Notifications } from '@/atoms';
import { Button, SellerBox, SubscriptionCard } from '@/molecules';
import { ProductForms } from '@/organisms/product';

import { InitPropsProvider } from '@/components/providers';
import { formatDateInterval, isOutOfStock, removeBaseURL } from '@/lib/utils';

import type { FunctionComponent } from 'react';
import type { TypeProductDescriptionProps } from './types';

const BundleOptions = dynamic(() => import('@/molecules/bundle-options'));

// TODO: Añadir soporte para distintos templates
/**
 * ProductDescription
 */
export const ProductDescription: FunctionComponent<
  TypeProductDescriptionProps
> = ({
  className = '',
  category = '',
  categoryLink = '',
  name,
  productId,
  description = '',
  price,
  regularPrice,
  moreInfo,
  attributes,
  type = 'simple',
  variations,
  template = '',
  stock,
  externalUrl,
  button,
  isMirakl,
  stockAlert = true,
  customizableBundleOptions,
  bundledItems,
  woosbIds,
  woosbData,
  exactQuantity,
  woosbMinQty,
  showSellerBox = true,
  subscriptionCard,
  metaData,
  sellerBox,
  sellerInfo,
}: TypeProductDescriptionProps) => {
  let intervalSellerClosedMessage = '';
  let isSellerClosed = false;

  if (sellerInfo?.closedFrom && sellerInfo.closedTo) {
    const closedFrom = new Date(sellerInfo.closedFrom);
    const closedTo = new Date(sellerInfo.closedTo);
    const now = new Date();

    closedFrom.setDate(closedFrom.getDate() + 1);
    closedTo.setDate(closedTo.getDate() + 1);

    if (now >= closedFrom && now <= closedTo) {
      intervalSellerClosedMessage = formatDateInterval(closedFrom, closedTo);
      isSellerClosed = true;
    }
  }

  const isSubscribable = !!metaData.find(
    (meta) => meta.key === '_non_subscribable' && meta.value === 'no',
  );

  const {
    options: { tribuAmantesArtesanos },
  } = useContext(InitPropsProvider);

  const isThisProductTribuApasionado =
    productId === tribuAmantesArtesanos?.product?.databaseId;

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex flex-col gap-2 py-4 lg:gap-6 lg:py-6">
        <div className="flex flex-col gap-3">
          <div className="flex justify-between">
            {category && categoryLink && (
              <NextLink href={categoryLink}>
                <a className="u-body u-body--s text-secondary-500">
                  {category}
                </a>
              </NextLink>
            )}
          </div>
          <h1 className="u-headline u-headline--h2">{name}</h1>
        </div>
        <div className="u-body u-body--s flex flex-col gap-2 text-typo-secondary">
          <div
            className="[&>*:not(:last-child,li)]:mb-2 [&>ol]:ml-5 [&>ol]:!list-decimal [&>ul]:ml-5 [&>ul]:!list-disc"
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {button?.label && button?.link?.url && (
            <NextLink href={removeBaseURL(button.link.url ?? '')} passHref>
              <Button
                negative={button?.negativo}
                as="a"
                variant={button?.variant ?? 'primary'}
                size={button?.size}
                className="min-w-full md:min-w-fit"
                {...(button?.showIcon && { icon: <Info /> })}
              >
                {button.label}
              </Button>
            </NextLink>
          )}
        </div>
      </div>

      {customizableBundleOptions?.isCustomizableBox && (
        <BundleOptions
          onCardAddProduct={customizableBundleOptions.onCardAddProduct}
        />
      )}

      <ProductForms
        type={type}
        bundledItems={bundledItems}
        woosbIds={woosbIds}
        stock={stock}
        template={template}
        price={price}
        productId={productId}
        regularPrice={regularPrice}
        moreInfo={moreInfo}
        isMirakl={isMirakl}
        externalUrl={externalUrl}
        stockAlert={stockAlert}
        attributes={attributes}
        variations={variations}
        isSellerClosed={isSellerClosed}
        exactQuantity={exactQuantity}
        woosbData={woosbData}
        woosbMinQty={woosbMinQty}
      />

      <div className="flex flex-col gap-2">
        {stock && isOutOfStock(stock) ? (
          <Notifications
            message={stockAlert ? 'Agotado temporalmente' : 'Agotado'}
            state="ERROR"
            box={false}
            Icon={Info}
          />
        ) : null}

        <Notifications
          message="La imagen puede diferir del producto por pequeñas modificaciones de diseño o lote."
          state="INFO"
          box={false}
          className="hidden lg:inline"
        />
      </div>

      <div className="mt-4 flex flex-col gap-6">
        {showSellerBox && sellerBox && (
          <SellerBox {...sellerBox}>
            {isSellerClosed && (
              <Notifications
                state="ERROR"
                box
                Icon={Info}
                message={`Lo sentimos. Este vendedor no suministra productos desde el ${intervalSellerClosedMessage}`}
              />
            )}
            {sellerBox.isSuspended && (
              <Notifications
                state="ERROR"
                box
                Icon={Info}
                message="Lo sentimos. Este vendedor no suministra productos actualmente"
              />
            )}
          </SellerBox>
        )}

        {isSubscribable &&
          subscriptionCard &&
          productId &&
          !isThisProductTribuApasionado && (
            <SubscriptionCard
              {...subscriptionCard}
              productId={productId}
              quantity={woosbMinQty || exactQuantity?.[0] || 1}
            />
          )}
      </div>
    </div>
  );
};
